/**
 * Load state for a ready-made avatar image (AvatarDisplay).
 *
 * The image walks its fallback chain (e.g. sad → angry → idle). If every URL in the chain fails —
 * usually a flaky connection on a phone — it waits and walks the chain once more, then settles on
 * the placeholder. Before this, the last failed image simply stayed on screen: an empty card
 * (specs/avatar-customizer-as-picker, "Image load failure").
 */
export type AvatarLoadPhase = 'loading' | 'waiting-retry' | 'failed'

export interface AvatarLoadState {
  phase: AvatarLoadPhase
  /** Index into the fallback chain currently being tried. */
  attemptIndex: number
  /** How many times the whole chain has been retried. */
  round: number
}

export const AVATAR_LOAD_RETRY_DELAY_MS = 3000
export const AVATAR_LOAD_MAX_RETRY_ROUNDS = 1

export const INITIAL_AVATAR_LOAD_STATE: AvatarLoadState = {
  phase: 'loading',
  attemptIndex: 0,
  round: 0,
}

/** The current image failed: try the next in the chain, or wait for a retry, or give up. */
export function advanceAvatarLoadOnError(
  state: AvatarLoadState,
  attemptsLength: number,
): AvatarLoadState {
  if (state.phase !== 'loading') return state
  if (state.attemptIndex + 1 < attemptsLength) {
    return { ...state, attemptIndex: state.attemptIndex + 1 }
  }
  if (state.round < AVATAR_LOAD_MAX_RETRY_ROUNDS) {
    return { ...state, phase: 'waiting-retry' }
  }
  return { ...state, phase: 'failed' }
}

/** The retry delay passed: walk the chain again from the start. */
export function retryAvatarLoad(state: AvatarLoadState): AvatarLoadState {
  if (state.phase !== 'waiting-retry') return state
  return { phase: 'loading', attemptIndex: 0, round: state.round + 1 }
}
